'use client';
import * as React from 'react';

export type Lang = 'ru' | 'en' | 'sr';
export const LANGS: Lang[] = ['ru', 'en', 'sr'];

const T = {
  tagline: {
    ru: 'Видеовстречи и уроки онлайн — без регистрации, прямо в браузере.',
    en: 'Video meetings and online lessons — no sign-up, right in your browser.',
    sr: 'Video sastanci i časovi onlajn — bez registracije, direktno u pregledaču.',
  },
  start: { ru: 'Начать встречу', en: 'Start a meeting', sr: 'Započni sastanak' },
  join: { ru: 'Войти по коду', en: 'Join with a code', sr: 'Uđi pomoću koda' },
  code: { ru: 'Код или ссылка встречи', en: 'Meeting code or link', sr: 'Kod ili link sastanka' },
  e2ee: { ru: 'Сквозное шифрование', en: 'End-to-end encryption', sr: 'Šifrovanje s kraja na kraj' },
  passphrase: { ru: 'Пароль шифрования', en: 'Passphrase', sr: 'Lozinka šifrovanja' },
  share: {
    ru: 'Ссылку на встречу можно отправить участникам — они войдут без регистрации.',
    en: 'Share the meeting link — participants join without signing up.',
    sr: 'Podelite link sastanka — učesnici ulaze bez registracije.',
  },
  platform: { ru: 'Платформа для учителей', en: 'Platform for teachers', sr: 'Platforma za nastavnike' },
  about: { ru: 'О продукте', en: 'About', sr: 'O proizvodu' },
  recAudio: { ru: 'Записывать звук', en: 'Record audio', sr: 'Snimaj zvuk' },
  recStop: { ru: 'Остановить запись звука', en: 'Stop audio recording', sr: 'Zaustavi snimanje zvuka' },
  autoOn: { ru: 'Кадр раз в минуту', en: 'Frame every minute', sr: 'Kadar svakog minuta' },
  autoOff: { ru: 'Выключить кадр раз в минуту', en: 'Turn off frame every minute', sr: 'Isključi kadar svakog minuta' },
  shot: { ru: 'Сделать скрин (PNG)', en: 'Take a screenshot (PNG)', sr: 'Napravi snimak ekrana (PNG)' },
  files: { ru: 'Записи встречи', en: 'Meeting recordings', sr: 'Snimci sastanka' },
  empty: { ru: 'Пока пусто', en: 'Nothing yet', sr: 'Još ništa' },
  saved: { ru: 'Скрин сохранён', en: 'Screenshot saved', sr: 'Snimak ekrana sačuvan' },
  recording: { ru: 'Идёт запись звука', en: 'Audio is being recorded', sr: 'Zvuk se snima' },
  frames: { ru: 'Кадры раз в минуту', en: 'Frames every minute', sr: 'Kadrovi svakog minuta' },
  download: { ru: 'Скачать', en: 'Download', sr: 'Preuzmi' },
  remove: { ru: 'Удалить', en: 'Delete', sr: 'Obriši' },
  close: { ru: 'Закрыть', en: 'Close', sr: 'Zatvori' },
  joinLive: { ru: 'Войти в эфир', en: 'Join the live', sr: 'Uđi u prenos uživo' },
  yourName: { ru: 'Ваше имя', en: 'Your name', sr: 'Vaše ime' },
  mic: { ru: 'Микрофон', en: 'Microphone', sr: 'Mikrofon' },
  cam: { ru: 'Камера', en: 'Camera', sr: 'Kamera' },
  failed: { ru: 'Не удалось', en: 'Failed', sr: 'Nije uspelo' },
} as const;
export type Key = keyof typeof T;

function detect(): Lang {
  try {
    const saved = localStorage.getItem('umno-lang') as Lang | null;
    if (saved && LANGS.includes(saved)) return saved;
  } catch {}
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'ru').toLowerCase();
  if (nav.startsWith('sr') || nav.startsWith('hr') || nav.startsWith('bs')) return 'sr';
  if (nav.startsWith('ru') || nav.startsWith('uk') || nav.startsWith('be')) return 'ru';
  return 'en';
}

export function useLang() {
  const [lang, setLangState] = React.useState<Lang>('ru');
  React.useEffect(() => setLangState(detect()), []);
  const setLang = (l: Lang) => {
    setLangState(l);
    try { localStorage.setItem('umno-lang', l); } catch {}
    document.documentElement.lang = l;
  };
  const t = React.useCallback((k: Key) => T[k][lang], [lang]);
  return { lang, setLang, t };
}

export function LangSwitch(props: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="umno-lang" role="group" aria-label="Language">
      {LANGS.map((l) => (
        <button key={l} aria-pressed={props.lang === l} onClick={() => props.setLang(l)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
